import React from 'react';

import { Link, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { GOOGLE_CHROME, KB_SUPPORTED_BROWSER, MS_EDGE } from '../constants/urls';
import browserWarning from '../images/browser-warning.svg';
import chromeLogo from '../images/chrome-logo.svg';
import edgeLogo from '../images/edge-logo.svg';
import { getUrlParams } from '../utils/url';

const StyledErrorContainer = styled.div`
    margin-top: 10px;
    text-align: center;
    width: 100%;
    height: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const BrowserButton = styled(Button)`
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 4px 16px;

    margin-top: 20px;
    margin-left: 20px;
    border-radius: 4px;
    background-color: white;
    width: 240px;
` as typeof Button;

const StyledErrorText = styled(Typography)`
    text-align: center;
    margin-top: 20px;
    margin-bottom: 10px;
`;

const ErrorBrowser = () => {
    const { t } = useTranslation('error_messages');
    const { browserName, browserFriendlyName, minVersion, unsupportedOS, ...launchParams } = getUrlParams();

    const friendlyName = React.useMemo(() => {
        if (!browserFriendlyName) {
            return '';
        }

        return browserFriendlyName.replaceAll(/\+/g, ' ');
    }, [browserFriendlyName]);

    const browserKey = React.useMemo(() => {
        if (unsupportedOS) {
            return 'browser-warning.unsupported-os';
        }

        if (browserName === 'chrome' || browserName === 'edge') {
            return 'browser-warning.browser-upgrade';
        }

        return 'browser-warning.unsupported-browser';
    }, [browserName, unsupportedOS]);

    const supportedBrowser = browserKey !== 'browser-warning.unsupported-browser';

    return (
        <StyledErrorContainer>
            <StyledErrorText variant="h1">{t('error_messages:browser-warning:title')}</StyledErrorText>
            <img src={browserWarning} role="presentation" alt="" />
            <StyledErrorText variant="body1">{t(browserKey, { friendlyName, minVersion })}</StyledErrorText>
            {(!supportedBrowser || browserName === 'chrome') && (
                <BrowserButton
                    variant="contained"
                    color="inherit"
                    href={GOOGLE_CHROME}
                    target="_blank"
                    rel="noreferrer"
                >
                    <img src={chromeLogo} role="presentation" alt="" />
                    <Typography variant="body1" color="primary">
                        {t('error_messages:browser-warning:browsers:chrome')}
                    </Typography>
                    {supportedBrowser && (
                        <Typography variant="body2" color="secondary">
                            {t('error_messages:browser-warning:update-version', { version: minVersion })}
                        </Typography>
                    )}
                </BrowserButton>
            )}
            {(!supportedBrowser || browserName === 'edge') && (
                <BrowserButton variant="contained" color="inherit" href={MS_EDGE} target="_blank" rel="noreferrer">
                    <img src={edgeLogo} role="presentation" alt="" />
                    <Typography variant="body1">{t('error_messages:browser-warning:browsers:edge')}</Typography>
                    {supportedBrowser && (
                        <Typography variant="body2">
                            {t('error_messages:browser-warning:update-version', { version: minVersion })}
                        </Typography>
                    )}
                </BrowserButton>
            )}
            {!supportedBrowser && (
                <StyledErrorText variant="body2">
                    <Link href={KB_SUPPORTED_BROWSER} target="_blank">
                        {t('error_messages:browser-warning:help-link')}
                    </Link>
                </StyledErrorText>
            )}
        </StyledErrorContainer>
    );
};

export default ErrorBrowser;
